import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductCardProps } from '../../types';
import Button from '../atoms/Button';
import './ProductCard.css';

const ProductCard: React.FC<ProductCardProps> = ({ 
  title,
  description,
  buttonText,
  buttonLink,
  imageSrc,
  imageAlt,
  reverse = false,
  backgroundColor = 'white'
}) => {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const cardElement = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, we can stop observing
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before element comes into view
      }
    );

    if (cardElement) {
      observer.observe(cardElement);
    }

    return () => {
      if (cardElement) {
        observer.unobserve(cardElement);
      }
    };
  }, []);

  const cardClasses = `product-card ${reverse ? 'product-card--reverse' : ''} ${isVisible ? 'product-card--visible' : ''}`;
  const contentClasses = `product-content product-content--${backgroundColor}`;
  const imageClasses = `product-image product-image--${backgroundColor}`;

  return (
    <div ref={cardRef} className={cardClasses}>
      <div className={contentClasses}>
        <div className="product-text">
          <h2 className="product-title">{title}</h2>
          <p className="product-description">{description}</p>
          <Button 
            variant="secondary" 
            size="medium"
            onClick={() => {
              if (buttonLink.startsWith('/')) {
                navigate(buttonLink);
              } else {
                window.location.href = buttonLink;
              }
            }}
            className="product-button"
          >
            {buttonText}
          </Button>
        </div>
      </div>
      
      <div className={imageClasses}>
        <img 
          src={imageSrc} 
          alt={imageAlt} 
          className="product-img"
          loading="lazy"
        />
      </div>
    </div>
  );
};

export default ProductCard;
